// src/components/gisDownload/GISDownloadModal.tsx
import React, { useState, useCallback, useRef, useMemo } from 'react';
import AppModal from '../common/AppModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner, faArrowLeft, faCheck } from '@fortawesome/free-solid-svg-icons';
import ContextHelp from '../../easymap/ContextHelp/ContextHelp';
import { usePermissions } from '../../context/PermissionContext';
import { AFF_FEATURE, AFF_EXPORT_OP, AFF_EXPORT_IDS } from '../../shared/affPermissions';

export interface GISDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormatOption {
  label: string;
  subtitle: string;
  format: string;
  crs: string;
}

interface LayerDef {
  id: string;
  label: string;
  category: 'operational' | 'var' | 'aff';
}

const FORMAT_OPTIONS: FormatOption[] = [
  {
    label: 'Shapefile (NWT Lambert)',
    subtitle: 'ESRI Shapefile, projected meters — EPSG:3580',
    format: 'shapefile',
    crs: '3580',
  },
  {
    label: 'Shapefile (WGS 84)',
    subtitle: 'ESRI Shapefile, lat/lon — EPSG:4326',
    format: 'shapefile',
    crs: '4326',
  },
  {
    label: 'GeoJSON (WGS 84)',
    subtitle: 'Standard web GIS format — EPSG:4326',
    format: 'geojson',
    crs: '4326',
  },
  {
    label: 'KML (WGS 84)',
    subtitle: 'Google Earth / Google Maps format — EPSG:4326',
    format: 'kml',
    crs: '4326',
  },
];

const AVAILABLE_LAYERS: LayerDef[] = [
  { id: 'wildfires', label: 'Wildfires', category: 'operational' },
  { id: 'lightning', label: 'Lightning Strikes (72hr)', category: 'operational' },
  { id: 'perimeters', label: 'Fire Perimeters (Current Year)', category: 'operational' },
  { id: 'smokes', label: 'Smokes', category: 'operational' },
  { id: 'stations', label: 'Weather Stations', category: 'operational' },
  { id: 'var_points', label: 'Values at Risk (Points)', category: 'var' },
  { id: 'var_areas', label: 'Values at Risk (Areas)', category: 'var' },
  { id: 'var_lines', label: 'Values at Risk (Lines)', category: 'var' },
  // Offered only with aff.export (#813, #866); the backend refuses them otherwise.
  { id: 'aff_aircraft', label: 'Aircraft (AFF)', category: 'aff' },
  { id: 'aff_tracks', label: 'Aircraft Tracks (AFF, 72hr)', category: 'aff' },
];

const CATEGORIES: Record<string, { label: string; icon: string; color: string }> = {
  operational: { label: 'Operational', icon: 'fa-solid fa-fire', color: '#d32f2f' },
  var: { label: 'Values at Risk', icon: 'fa-solid fa-triangle-exclamation', color: '#e65100' },
  aff: { label: 'Aircraft', icon: 'fa-solid fa-plane', color: '#1565c0' },
};

const S: Record<string, React.CSSProperties> = {
  wrapper: {
    padding: '4px 4px 0',
    color: 'var(--em3-color-text-primary)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    margin: '0 0 16px 0',
    fontSize: 'var(--em3-font-size-xl)',
    fontWeight: 600,
    color: 'var(--em3-color-text-primary)',
    borderBottom: '1px solid var(--em3-color-border)',
    paddingBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '16px',
  },
  footer: {
    fontSize: '12px',
    color: 'var(--em3-color-text-secondary)',
    borderTop: '1px solid var(--em3-color-border)',
    paddingTop: '12px',
    marginTop: '4px',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--em3-color-text-secondary)',
    fontSize: '14px',
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '12px',
  },
  catHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 0',
    fontSize: '13px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    borderBottom: '1px solid var(--em3-color-border)',
    marginBottom: '4px',
    marginTop: '8px',
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 8px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  exportBtn: {
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    fontWeight: 600,
    backgroundColor: 'var(--em3-color-primary)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--em3-border-radius-lg)',
    cursor: 'pointer',
    marginTop: '12px',
  },
  selectedFormat: {
    fontSize: '12px',
    color: 'var(--em3-color-text-secondary)',
    marginBottom: '8px',
  },
  counter: {
    fontSize: '13px',
    color: 'var(--em3-color-text-secondary)',
    marginBottom: '8px',
  },
  successBox: {
    padding: '16px',
    backgroundColor: 'var(--em3-color-background-main)',
    border: '1px solid var(--em3-color-border)',
    borderRadius: 'var(--em3-border-radius-lg)',
    textAlign: 'center' as const,
  },
};

type Step = 'format' | 'layers' | 'downloading' | 'done';

export const GISDownloadModal: React.FC<GISDownloadModalProps> = ({ isOpen, onClose }) => {
  const { canDo } = usePermissions();
  const mayExportAff = canDo(AFF_FEATURE, AFF_EXPORT_OP);
  // The datasets this user may take. "All" (no layers list) is read the same
  // way by the backend, which leaves AFF out for anyone without aff.export.
  const layers = useMemo(
    () => AVAILABLE_LAYERS.filter(l => mayExportAff || !AFF_EXPORT_IDS.includes(l.id)),
    [mayExportAff],
  );
  const [step, setStep] = useState<Step>('format');
  const [selectedFormat, setSelectedFormat] = useState<FormatOption | null>(null);
  const [selectedLayers, setSelectedLayers] = useState<Set<string>>(
    () => new Set(layers.map(l => l.id))
  );
  const downloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reset = useCallback(() => {
    setStep('format');
    setSelectedFormat(null);
    setSelectedLayers(new Set(layers.map(l => l.id)));
    if (downloadTimerRef.current) clearTimeout(downloadTimerRef.current);
  }, [layers]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleFormatSelect = useCallback((opt: FormatOption) => {
    setSelectedFormat(opt);
    setStep('layers');
  }, []);

  const toggleLayer = useCallback((id: string) => {
    setSelectedLayers(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleCategory = useCallback((category: string) => {
    const catLayerIds = layers.filter(l => l.category === category).map(l => l.id);
    setSelectedLayers(prev => {
      const next = new Set(prev);
      const allSelected = catLayerIds.every(id => next.has(id));
      if (allSelected) {
        catLayerIds.forEach(id => next.delete(id));
      } else {
        catLayerIds.forEach(id => next.add(id));
      }
      return next;
    });
  }, [layers]);

  const handleDownload = useCallback(() => {
    if (!selectedFormat || selectedLayers.size === 0) return;
    setStep('downloading');

    const layerParam = selectedLayers.size < layers.length
      ? `&layers=${Array.from(selectedLayers).join(',')}`
      : '';
    const a = document.createElement('a');
    a.href = `/api/gis/download?format=${selectedFormat.format}&crs=${selectedFormat.crs}${layerParam}`;
    a.download = '';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    downloadTimerRef.current = setTimeout(() => setStep('done'), 3000);
  }, [selectedFormat, selectedLayers, layers]);

  const renderFormatCard = (opt: FormatOption) => {
    const cardStyle: React.CSSProperties = {
      backgroundColor: 'var(--em3-color-background-main)',
      border: '1px solid var(--em3-color-border)',
      borderRadius: 'var(--em3-border-radius-lg)',
      padding: '14px 16px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      transition: 'border-color 0.15s',
      color: 'var(--em3-color-text-primary)',
    };

    return (
      <button
        key={opt.format + opt.crs}
        style={cardStyle}
        onClick={() => handleFormatSelect(opt)}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--em3-color-primary)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--em3-color-border)'; }}
      >
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '3px' }}>{opt.label}</div>
          <div style={{ fontSize: '12px', color: 'var(--em3-color-text-secondary)' }}>{opt.subtitle}</div>
        </div>
        <FontAwesomeIcon icon={faDownload} style={{ fontSize: '16px', flexShrink: 0 }} />
      </button>
    );
  };

  const renderCategory = (catKey: string) => {
    const catLayers = layers.filter(l => l.category === catKey);
    if (catLayers.length === 0) return null;
    const info = CATEGORIES[catKey];
    const allSelected = catLayers.every(l => selectedLayers.has(l.id));
    const someSelected = catLayers.some(l => selectedLayers.has(l.id));

    return (
      <div key={catKey}>
        <div style={S.catHeader}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: info.color }}>
            <input
              type="checkbox"
              checked={allSelected}
              ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
              onChange={() => toggleCategory(catKey)}
            />
            <i className={info.icon} />
            {info.label} ({catLayers.length})
          </label>
        </div>
        {catLayers.map(layer => (
          <label
            key={layer.id}
            style={{
              ...S.checkboxItem,
              backgroundColor: selectedLayers.has(layer.id) ? 'var(--em3-color-background-panel, #fafafa)' : 'transparent',
            }}
          >
            <input
              type="checkbox"
              checked={selectedLayers.has(layer.id)}
              onChange={() => toggleLayer(layer.id)}
              style={{ marginRight: '8px' }}
            />
            <span style={{ flex: 1 }}>{layer.label}</span>
          </label>
        ))}
      </div>
    );
  };

  return (
    <AppModal isOpen={isOpen} onClose={handleClose} width={480}>
      <div style={S.wrapper}>
        <h2 style={S.header}>
          Download GIS Data
          <ContextHelp title='Help with GIS Downloads' context='gis-download-help' />
        </h2>

        {/* Step 1: Format selection */}
        {step === 'format' && (
          <>
            <div style={S.cardList}>
              {FORMAT_OPTIONS.map(renderFormatCard)}
            </div>
            <div style={S.footer}>
              Select a format, then choose which datasets to include.<br />
              Includes: Wildfires, Lightning (72hr), Fire Perimeters, Weather Stations, Values at Risk
            </div>
          </>
        )}

        {/* Step 2: Layer selection */}
        {step === 'layers' && selectedFormat && (
          <>
            <button style={S.backBtn} onClick={() => setStep('format')}>
              <FontAwesomeIcon icon={faArrowLeft} /> Change format
            </button>
            <div style={S.selectedFormat}>
              Format: <strong>{selectedFormat.label}</strong>
            </div>
            <div style={S.counter}>
              {selectedLayers.size} of {layers.length} datasets selected
            </div>

            {Object.keys(CATEGORIES).map(renderCategory)}

            <button
              style={{
                ...S.exportBtn,
                ...(selectedLayers.size === 0 ? { backgroundColor: 'var(--em3-color-text-secondary)', cursor: 'not-allowed' } : {}),
              }}
              onClick={handleDownload}
              disabled={selectedLayers.size === 0}
            >
              <FontAwesomeIcon icon={faDownload} style={{ marginRight: '8px' }} />
              Export {selectedLayers.size} Dataset{selectedLayers.size !== 1 ? 's' : ''}
            </button>
          </>
        )}

        {/* Step 3: Downloading */}
        {step === 'downloading' && (
          <div style={{ ...S.successBox, padding: '40px 16px' }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '32px', marginBottom: '12px', color: 'var(--em3-color-primary)' }} />
            <div style={{ fontSize: '15px', fontWeight: 600 }}>Generating export...</div>
            <div style={{ fontSize: '13px', color: 'var(--em3-color-text-secondary)', marginTop: '8px' }}>
              {selectedLayers.size} dataset{selectedLayers.size !== 1 ? 's' : ''} as {selectedFormat?.label}
            </div>
          </div>
        )}

        {/* Step 4: Done */}
        {step === 'done' && (
          <div style={S.successBox}>
            <FontAwesomeIcon icon={faCheck} style={{ fontSize: '32px', color: '#4caf50', marginBottom: '12px' }} />
            <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px' }}>Export Ready!</div>
            <div style={{ fontSize: '13px', color: 'var(--em3-color-text-secondary)', marginBottom: '16px' }}>
              Your download should start automatically.
            </div>
            <button
              style={{ ...S.exportBtn, backgroundColor: 'transparent', color: 'var(--em3-color-primary)', border: '2px solid var(--em3-color-primary)' }}
              onClick={reset}
            >
              Export More
            </button>
          </div>
        )}
      </div>
    </AppModal>
  );
};

export default GISDownloadModal;
